import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import YhButton from "../../ui/YhButton";
import YhInput from "../../ui/YhInput";
import YhModal from "../../ui/YhModal";
import YhSelect, { buildStageSelectGroups, YhSelectGroup } from "../../ui/YhSelect";
import YhMultiSelect from "../../ui/YhMultiSelect";
import { YhDateRangePicker, YhDateRangeValue } from "../../ui/YhDateTimePicker";
import { selectActiveOrganizationId } from "../../redux/authSlice";
import { store } from "../../utils/store";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import { clickToCall } from "../Team/components/ShowAll/ShowAllService";
import {
  refreshActiveIvrCalls,
  seedActiveIvrCall,
  useActiveIvrCalls,
} from "../../utils/useActiveIvrCalls";
import { setUuid } from "../../redux/authSlice";
import { fetchAutomationRoutingOptions } from "../../services/organizationService";
import {
  assignCallDatasetRows,
  convertCallDatasetRows,
  listCallDatasetRows,
  listCallDatasets,
  updateCallDatasetStage,
} from "../../services/callDatasetService";
import urls from "../../global/constants/UrlConstants";
import { getStage } from "../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";

type Row = {
  _id: string;
  leadName?: string;
  leadPhone?: string;
  leadEmail?: string;
  projectUid?: string;
  stage?: string;
  source?: string;
  AssignTo?: string[];
  AssignToUser?: string[];
  assignedTeamNames?: string[];
  assignedUserNames?: string[];
  convertedLeadId?: string;
  createdAt?: string;
  datasetName?: string;
};

type Opt = { label: string; value: string };
type TeamOpt = Opt & { departmentId?: string };
type MemberOpt = Opt & { teamIds: string[] };

type Scope = {
  orgWide?: boolean;
  leader?: boolean;
  member?: boolean;
  teamIds?: string[];
  userId?: string;
};

type AppliedFilters = {
  stage: string;
  dateRange: YhDateRangeValue;
  departmentId: string;
  teamId: string;
  memberId: string;
  uniqueness: "" | "unique" | "duplicate";
  sort: "newest" | "oldest" | "name_asc" | "name_desc";
};

const emptyFilters = (): AppliedFilters => ({
  stage: "",
  dateRange: { from: null, to: null },
  departmentId: "",
  teamId: "",
  memberId: "",
  uniqueness: "",
  sort: "newest",
});

function dmyToIso(value: string | null | undefined) {
  if (!value) return "";
  const parts = value.split("-");
  if (parts.length === 3 && parts[0].length <= 2) {
    const [d, m, y] = parts;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return value;
}

function pageNumbers(page: number, pageCount: number) {
  const maxButtons = 5;
  if (pageCount <= maxButtons) {
    return Array.from({ length: pageCount }, (_, i) => i);
  }
  let start = Math.max(0, page - 2);
  const end = Math.min(pageCount - 1, start + maxButtons - 1);
  start = Math.max(0, end - maxButtons + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

const CallDatasetsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const activeOrgId = useSelector(selectActiveOrganizationId);
  const orgId =
    activeOrgId && activeOrgId !== "all" && activeOrgId !== "*"
      ? String(activeOrgId)
      : "";
  const params = new URLSearchParams(location.search);
  const datasetName = params.get("dataset") || "";

  const [datasets, setDatasets] = useState<
    { datasetName: string; count: number }[]
  >([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [scope, setScope] = useState<Scope>({});
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<AppliedFilters>(emptyFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [departments, setDepartments] = useState<Opt[]>([]);
  const [teams, setTeams] = useState<TeamOpt[]>([]);
  const [members, setMembers] = useState<MemberOpt[]>([]);
  const [stageGroups, setStageGroups] = useState<YhSelectGroup[]>([]);
  const [busy, setBusy] = useState(false);
  const [datasetsBusy, setDatasetsBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [convertAsk, setConvertAsk] = useState<string[]>([]);
  const [converting, setConverting] = useState(false);
  const { hasActiveCall, primary } = useActiveIvrCalls();

  const loadDatasets = async () => {
    if (!orgId) return;
    setDatasetsBusy(true);
    try {
      const res = await listCallDatasets(orgId);
      setDatasets(Array.isArray(res?.data) ? res.data : []);
    } finally {
      setDatasetsBusy(false);
    }
  };

  const loadRows = async (nextPage = page, nextSize = pageSize) => {
    if (!orgId || !datasetName) {
      setRows([]);
      setTotal(0);
      return;
    }
    setBusy(true);
    try {
      const res = await listCallDatasetRows({
        organizationId: orgId,
        datasetName,
        page: nextPage,
        limit: nextSize,
        stage: filters.stage,
        from: dmyToIso(filters.dateRange.from),
        to: dmyToIso(filters.dateRange.to),
        search,
        uniqueness: filters.uniqueness,
        sort: filters.sort,
        departmentId: scope.orgWide ? filters.departmentId : "",
        teamId: scope.member ? "" : filters.teamId,
        memberId: scope.member ? "" : filters.memberId,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setTotal(Number(res?.metadata?.total || 0));
      if (res?.scope) setScope(res.scope);
      setPage(nextPage);
      setSelected([]);
    } catch (e: any) {
      openErrorNotification(e?.message || "Could not load dataset");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    loadDatasets().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadRows(0, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, datasetName, search, filters, pageSize]);

  const teamKey = (scope.teamIds || []).join(",");
  useEffect(() => {
    if (!orgId) return;
    if (!filtersOpen && !assignOpen) return;
    getStage()
      .then((res: any) => {
        const groups = buildStageSelectGroups(res?.data || []);
        if (groups.length) setStageGroups(groups);
      })
      .catch(() => undefined);
  }, [orgId, filtersOpen, assignOpen]);

  const applyRouting = (data: any) => {
    const allow =
      scope.orgWide || !scope.teamIds?.length
        ? null
        : new Set(scope.teamIds.map(String));
    const teamRows = (data.teams || []).filter(
      (t: any) => allow == null || allow.has(String(t.teamId))
    );
    if (Array.isArray(data.departments)) {
      setDepartments(
        scope.orgWide
          ? data.departments.map((d: any) => ({
              label: d.name,
              value: String(d._id),
            }))
          : []
      );
    }
    if (Array.isArray(data.teams)) {
      setTeams(
        teamRows.map((t: any) => ({
          label: t.teamName || t.teamId,
          value: String(t.teamId),
          departmentId: t.departmentId ? String(t.departmentId) : "",
        }))
      );
    }
    if (Array.isArray(data.members) && data.members.length) {
      setMembers(
        data.members
          .filter(
            (m: any) =>
              allow == null ||
              (m.teamIds || []).some((id: string) => allow.has(String(id)))
          )
          .map((m: any) => ({
            label: m.name || m.userId,
            value: String(m.userId),
            teamIds: (m.teamIds || []).map(String),
          }))
      );
    }
  };

  const loadRouting = (teamIds?: string[]) => {
    if (!orgId || scope.member) return;
    const ids = (teamIds || []).filter(Boolean);
    fetchAutomationRoutingOptions(orgId, {
      lite: true,
      ...(ids.length
        ? { teamIds: ids }
        : scope.leader && scope.teamIds?.length
          ? { teamIds: scope.teamIds }
          : {}),
    })
      .then((res) => applyRouting(res?.data || {}))
      .catch(() => undefined);
  };

  useEffect(() => {
    if (!orgId || scope.member) return;
    if (!filtersOpen && !assignOpen) return;
    loadRouting(scope.leader ? scope.teamIds : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, scope.orgWide, scope.member, scope.leader, teamKey, filtersOpen, assignOpen]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.stage) n += 1;
    if (filters.uniqueness) n += 1;
    if (filters.sort && filters.sort !== "newest") n += 1;
    if (filters.dateRange.from || filters.dateRange.to) n += 1;
    if (scope.orgWide && filters.departmentId) n += 1;
    if (!scope.member && filters.teamId) n += 1;
    if (!scope.member && filters.memberId) n += 1;
    return n;
  }, [filters, scope.orgWide, scope.member]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const fromRow = total === 0 ? 0 : page * pageSize + 1;
  const toRow = Math.min(total, (page + 1) * pageSize);

  const callRow = async (row: Row) => {
    if (hasActiveCall) {
      openErrorNotification(
        `Call already in progress (${primary?.durationFormatted || "live"}). Wait until it ends.`
      );
      return;
    }
    const auth = store.getState().auth || {};
    const uuid = auth.uuid;
    if (!uuid) {
      openErrorNotification("Caller ID (uuid) missing for this user");
      return;
    }
    if (!row.leadPhone) {
      openErrorNotification("Valid phone number required to call");
      return;
    }
    try {
      const response = await clickToCall({
        leadPhone: row.leadPhone,
        leadName: row.leadName || "",
        agentName: auth.userName || "",
        uuid,
      });
      if (
        response?.success === 0 ||
        response?.success === false ||
        response?.error
      ) {
        openErrorNotification(response?.message || response?.error || "Call failed");
        return;
      }
      if (response?.uuid && String(response.uuid) !== String(uuid)) {
        store.dispatch(setUuid(String(response.uuid)));
      }
      openSuccessNotification(response?.message || "Call initiated");
      seedActiveIvrCall({
        _id: response?.activeCallId || response?.data?.activeCallId,
        status: "initiated",
        leadName: row.leadName || "",
        customerNumber: row.leadPhone,
        agentUserId: response?.uuid || uuid,
        agentCrmUserId: auth.userId,
        uniqueCallId: response?.unique_id || response?.data?.unique_id,
        referenceId: response?.reference_id || response?.referenceId,
      });
      await refreshActiveIvrCalls();
    } catch (e: any) {
      openErrorNotification(e?.message || "Call failed");
    }
  };

  const askConvert = (ids: string[]) => {
    if (!ids.length) {
      openErrorNotification("Select at least one row");
      return;
    }
    setConvertAsk(ids);
  };

  const confirmConvert = async () => {
    const ids = convertAsk;
    if (!ids.length) return;
    setConverting(true);
    try {
      const res = await convertCallDatasetRows(ids);
      const created = (res?.converted || []).filter(
        (item: any) => !item.already && !item.linked
      ).length;
      const linked = (res?.converted || []).filter((item: any) => item.linked).length;
      if (created || linked) {
        openSuccessNotification(res?.message || "Converted");
      }
      if (res?.errors?.length) {
        const text = res.errors
          .map((item: any) => String(item?.message || "").replace(/E11000[\s\S]*/i, "").trim())
          .filter(Boolean)
          .slice(0, 3)
          .join(" ");
        openErrorNotification(
          text || "Some rows could not be converted."
        );
      } else if (!created && !linked) {
        openErrorNotification(res?.message || "No rows were converted.");
      }
      setConvertAsk([]);
      loadRows(page);
    } catch (e: any) {
      const raw = String(e?.message || "");
      openErrorNotification(
        /duplicate key|E11000|leadPhone/i.test(raw)
          ? "This phone number is already a lead in this organization."
          : raw || "Convert failed"
      );
    } finally {
      setConverting(false);
    }
  };

  const openAssign = () => setAssignOpen(true);

  return (
    <div className="box-border w-full space-y-4 px-4 py-4 md:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#111827]">Call Datasets</h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Call, update stage, assign, and convert rows from here.
          </p>
        </div>
        {!scope.member ? (
          <YhButton onClick={() => history.push(urls.callDatasetUploadPath)}>
            Upload into dataset
          </YhButton>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="h-fit rounded-2xl border border-[#EEF0F5] bg-white p-3 shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
          <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
            Datasets
          </p>
          {datasetsBusy && datasets.length === 0 ? (
            <div className="flex items-center gap-2 px-2 py-3 text-xs font-medium text-[#841C39]">
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#E8D0D8] border-t-[#841C39]" />
              Loading datasets
            </div>
          ) : datasets.length === 0 ? (
            <p className="px-2 text-xs text-[#6B7280]">No datasets yet.</p>
          ) : (
            datasets.map((set) => (
              <button
                key={set.datasetName}
                type="button"
                className={`mb-1 block w-full rounded-xl px-3 py-2 text-left text-sm ${
                  set.datasetName === datasetName
                    ? "bg-[#F9F0F4] font-semibold text-[#841C39]"
                    : "text-[#111827] hover:bg-[#F9FAFB]"
                }`}
                onClick={() =>
                  history.push(
                    `${urls.callDatasetsPath}?dataset=${encodeURIComponent(set.datasetName)}`
                  )
                }
              >
                {set.datasetName}
                <span className="ml-1 text-xs font-normal text-[#6B7280]">
                  ({set.count})
                </span>
              </button>
            ))
          )}
        </aside>

        <section className="min-w-0">
          {!datasetName ? (
            <p className="text-sm text-[#6B7280]">Select a dataset.</p>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
              <div className="flex flex-col gap-2.5 border-b border-[#EEF0F5] px-4 py-3 desktop:flex-row desktop:items-center desktop:justify-between">
                <div className="w-full min-w-0 desktop:max-w-[360px]">
                  <YhInput
                    placeholder="Search name or phone"
                    value={searchInput}
                    type="search"
                    onChange={(e) => setSearchInput(e.target.value)}
                    containerClassName="w-full"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFiltersOpen(true)}
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3.5 text-sm font-semibold text-[#374151] transition hover:border-[#D4B8C0] hover:bg-[#F8FAFC]"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                    </svg>
                    Filters
                    {activeFilterCount > 0 ? (
                      <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#A02448] px-1.5 text-[11px] font-bold text-white">
                        {activeFilterCount}
                      </span>
                    ) : null}
                  </button>
                  {activeFilterCount > 0 ? (
                    <button
                      type="button"
                      onClick={() => setFilters(emptyFilters())}
                      className="px-2 text-sm font-semibold text-[#A02448] hover:underline"
                    >
                      Clear All
                    </button>
                  ) : null}
                  {!scope.member ? (
                    <>
                      <YhButton variant="secondary" size="sm" onClick={openAssign}>
                        Assign
                      </YhButton>
                      <YhButton size="sm" onClick={() => askConvert(selected)}>
                        Convert selected
                      </YhButton>
                    </>
                  ) : null}
                </div>
              </div>

              {scope.member ? (
                <p className="border-b border-[#EEF0F5] px-4 py-2 text-xs text-[#6B7280]">
                  You see only rows assigned to you. You can call and change stage.
                </p>
              ) : null}

              {busy ? (
                <div className="h-1 w-full overflow-hidden bg-[#F3E8EC]">
                  <div className="h-full w-1/3 animate-pulse bg-[#841C39]" />
                </div>
              ) : null}

              <div className="overflow-auto">
                <table className="w-full min-w-[860px] border-collapse text-left">
                  <thead>
                    <tr className="border-b border-[#EEF0F5]">
                      <th className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={rows.length > 0 && selected.length === rows.length}
                          onChange={(e) =>
                            setSelected(e.target.checked ? rows.map((r) => r._id) : [])
                          }
                        />
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Name</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Phone</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Project id</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Assigned to</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Stage</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {busy && !rows.length ? (
                      <tr>
                        <td className="px-4 py-16 text-center" colSpan={7}>
                          <div className="inline-flex items-center gap-2 text-sm font-medium text-[#841C39]">
                            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#E8D0D8] border-t-[#841C39]" />
                            Loading dataset
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    {rows.map((row) => (
                      <tr key={row._id} className="border-b border-[#F3F4F6]">
                        <td className="px-4 py-3 align-middle">
                          <input
                            type="checkbox"
                            checked={selected.includes(row._id)}
                            onChange={(e) =>
                              setSelected((prev) =>
                                e.target.checked
                                  ? [...prev, row._id]
                                  : prev.filter((id) => id !== row._id)
                              )
                            }
                          />
                        </td>
                        <td className="px-4 py-3 align-middle text-sm font-medium text-[#111827]">
                          {row.leadName || "—"}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-[#111827]">
                          {row.leadPhone || "—"}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-[#111827]">
                          {row.projectUid || "—"}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-[#111827]">
                          {(row.assignedUserNames || []).join(", ") || "—"}
                          {(row.assignedTeamNames || []).length ? (
                            <span className="mt-0.5 block text-xs text-[#6B7280]">
                              {(row.assignedTeamNames || []).join(", ")}
                            </span>
                          ) : null}
                        </td>
                        <td className="w-[220px] px-4 py-2 align-middle">
                          <div className="relative w-[200px] overflow-hidden">
                            <YhSelect
                              className="!w-full"
                              placeholder={row.stage || "Select stage"}
                              searchPlaceholder="Search stages..."
                              searchable
                              showOptionIcon={false}
                              value={row.stage || ""}
                              groups={stageGroups}
                              onChange={async (e) => {
                                const next = String(e.target.value || "");
                                if (!next || next === row.stage) return;
                                try {
                                  await updateCallDatasetStage(row._id, next);
                                  setRows((prev) =>
                                    prev.map((item) =>
                                      item._id === row._id ? { ...item, stage: next } : item
                                    )
                                  );
                                } catch (err: any) {
                                  openErrorNotification(err?.message || "Stage update failed");
                                }
                              }}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <div className="flex items-center gap-3 whitespace-nowrap">
                            <button
                              type="button"
                              className="text-sm font-semibold text-[#841C39] hover:underline"
                              onClick={() => callRow(row)}
                            >
                              IVR call
                            </button>
                            {row.convertedLeadId ? (
                              <span className="text-xs font-semibold text-[#065F46]">
                                Converted
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="text-sm font-semibold text-[#111827] hover:underline"
                                onClick={() => askConvert([row._id])}
                              >
                                Convert
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!busy && !rows.length ? (
                      <tr>
                        <td className="px-4 py-10 text-center text-sm text-[#6B7280]" colSpan={7}>
                          No rows in this dataset for your login.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#EEF0F5] px-4 py-3 text-sm text-[#6B7280]">
                <span>
                  Showing{" "}
                  <span className="font-semibold text-[#111827]">{fromRow}</span> to{" "}
                  <span className="font-semibold text-[#111827]">{toRow}</span> of{" "}
                  <span className="font-semibold text-[#111827]">
                    {total.toLocaleString()}
                  </span>{" "}
                  rows
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs">Rows per page</span>
                  <select
                    className="rounded-xl border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-xs outline-none focus:border-[#A02448]"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={page <= 0}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-[#E5E7EB] hover:bg-[#F3F4F6] disabled:opacity-40"
                      onClick={() => loadRows(page - 1)}
                      aria-label="Previous page"
                    >
                      ‹
                    </button>
                    {pageNumbers(page, pageCount).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => loadRows(p)}
                        className={`inline-flex h-8 min-w-[32px] items-center justify-center rounded-full px-2 text-xs font-semibold ${
                          p === page
                            ? "bg-[#A02448] text-white"
                            : "text-[#374151] hover:bg-[#F3F4F6]"
                        }`}
                      >
                        {p + 1}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={page + 1 >= pageCount}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-[#E5E7EB] hover:bg-[#F3F4F6] disabled:opacity-40"
                      onClick={() => loadRows(Math.min(pageCount - 1, page + 1))}
                      aria-label="Next page"
                    >
                      ›
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      <FilterDrawer
        open={filtersOpen}
        scope={scope}
        value={filters}
        stageGroups={stageGroups}
        departments={departments}
        teams={teams}
        members={members}
        onTeamChange={(teamId) => loadRouting(teamId ? [teamId] : undefined)}
        onClose={() => setFiltersOpen(false)}
        onApply={(next) => {
          setFilters(next);
          setFiltersOpen(false);
        }}
        onClear={() => {
          setFilters(emptyFilters());
          setFiltersOpen(false);
        }}
      />

      <AssignModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        selectedCount={selected.length}
        total={total}
        scope={scope}
        departments={departments}
        teams={teams}
        members={members}
        onTeamsChange={(ids) => loadRouting(ids)}
        onAssign={async (mode, teamIds, userIds) => {
          if (mode === "selected" && !selected.length) {
            openErrorNotification("Select at least one row");
            return;
          }
          if (!teamIds.length && !userIds.length) {
            openErrorNotification("Select a team or a member");
            return;
          }
          try {
            const res = await assignCallDatasetRows({
              mode,
              ids: mode === "selected" ? selected : [],
              datasetName,
              organizationId: orgId,
              teamIds,
              userIds,
              stage: filters.stage,
              from: dmyToIso(filters.dateRange.from),
              to: dmyToIso(filters.dateRange.to),
              search,
              uniqueness: filters.uniqueness,
              sort: filters.sort,
              departmentId: filters.departmentId,
              teamId: filters.teamId,
              memberId: filters.memberId,
            });
            openSuccessNotification(res?.message || "Assigned");
            setAssignOpen(false);
            loadRows(page);
          } catch (e: any) {
            openErrorNotification(e?.message || "Assign failed");
          }
        }}
      />

      <YhModal
        open={convertAsk.length > 0}
        onClose={() => {
          if (!converting) setConvertAsk([]);
        }}
        title="Convert to leads"
        width={460}
        footer={
          <>
            <YhButton
              variant="secondary"
              size="sm"
              disabled={converting}
              onClick={() => setConvertAsk([])}
            >
              Cancel
            </YhButton>
            <YhButton size="sm" loading={converting} onClick={confirmConvert}>
              Convert
            </YhButton>
          </>
        }
      >
        <p className="text-sm text-[#374151]">
          Convert{" "}
          <span className="font-semibold text-[#111827]">{convertAsk.length}</span>{" "}
          row{convertAsk.length === 1 ? "" : "s"} into leads?
        </p>
        <p className="mt-2 text-sm text-[#6B7280]">
          If a phone number is already a lead, that row is linked to the existing
          lead instead of creating a second one.
        </p>
      </YhModal>
    </div>
  );
};

function FilterDrawer({
  open,
  scope,
  value,
  stageGroups,
  departments,
  teams,
  members,
  onTeamChange,
  onClose,
  onApply,
  onClear,
}: {
  open: boolean;
  scope: Scope;
  value: AppliedFilters;
  stageGroups: YhSelectGroup[];
  departments: Opt[];
  teams: TeamOpt[];
  members: MemberOpt[];
  onTeamChange?: (teamId: string) => void;
  onClose: () => void;
  onApply: (next: AppliedFilters) => void;
  onClear: () => void;
}) {
  const [draft, setDraft] = useState<AppliedFilters>(value);
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const teamOptions = draft.departmentId
    ? teams.filter((t) => t.departmentId === draft.departmentId)
    : teams;
  const memberOptions = draft.teamId
    ? members.filter((m) => m.teamIds.includes(draft.teamId))
    : scope.leader
      ? members
      : [];

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1300] flex justify-end">
      <button
        type="button"
        aria-label="Close filters"
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Call dataset filters"
        className="relative z-[1] flex h-full w-full max-w-[420px] flex-col bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-[#111827]">Filters</h2>
            <p className="text-xs text-[#6B7280]">Refine this dataset</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-[#6B7280] hover:bg-[#F3F4F6]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <YhSelect
            label="Rows"
            placeholder="All rows"
            clearable={false}
            showOptionIcon={false}
            className="!w-full"
            value={draft.uniqueness}
            options={[
              { label: "All rows", value: "" },
              { label: "Unique phones", value: "unique" },
              { label: "Duplicate phones", value: "duplicate" },
            ]}
            onChange={(e: any) =>
              setDraft((prev) => ({
                ...prev,
                uniqueness: String(e.target.value || "") as AppliedFilters["uniqueness"],
              }))
            }
          />
          <YhSelect
            label="Sort"
            clearable={false}
            showOptionIcon={false}
            className="!w-full"
            value={draft.sort}
            options={[
              { label: "Newest first", value: "newest" },
              { label: "Oldest first", value: "oldest" },
              { label: "Name A–Z", value: "name_asc" },
              { label: "Name Z–A", value: "name_desc" },
            ]}
            onChange={(e: any) =>
              setDraft((prev) => ({
                ...prev,
                sort: (String(e.target.value || "newest") ||
                  "newest") as AppliedFilters["sort"],
              }))
            }
          />
          <YhSelect
            label="Stage"
            placeholder="All stages"
            searchPlaceholder="Search stages..."
            searchable
            clearable
            showOptionIcon={false}
            className="!w-full"
            value={draft.stage}
            groups={stageGroups}
            onChange={(e: any) =>
              setDraft((prev) => ({ ...prev, stage: String(e.target.value || "") }))
            }
          />
          <YhDateRangePicker
            label="Date range"
            value={draft.dateRange}
            onChange={(range) => setDraft((prev) => ({ ...prev, dateRange: range }))}
          />
          {scope.orgWide ? (
            <YhSelect
              label="Department"
              placeholder="All departments"
              searchable
              clearable
              showOptionIcon={false}
              className="!w-full"
              value={draft.departmentId}
              options={departments}
              onChange={(e: any) =>
                setDraft((prev) => ({
                  ...prev,
                  departmentId: String(e.target.value || ""),
                  teamId: "",
                  memberId: "",
                }))
              }
            />
          ) : null}
          {!scope.member ? (
            <YhSelect
              label="Team"
              placeholder={scope.leader ? "Your teams" : "All teams"}
              searchable
              clearable
              showOptionIcon={false}
              className="!w-full"
              value={draft.teamId}
              options={teamOptions}
              onChange={(e: any) => {
                const teamId = String(e.target.value || "");
                onTeamChange?.(teamId);
                setDraft((prev) => ({
                  ...prev,
                  teamId,
                  memberId: "",
                }));
              }}
            />
          ) : null}
          {!scope.member && (scope.leader || draft.teamId) ? (
            <YhSelect
              label="Member"
              placeholder="All members"
              searchable
              clearable
              showOptionIcon={false}
              className="!w-full"
              value={draft.memberId}
              options={memberOptions}
              onChange={(e: any) =>
                setDraft((prev) => ({
                  ...prev,
                  memberId: String(e.target.value || ""),
                }))
              }
            />
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-[#F3F4F6] px-4 py-3">
          <button
            type="button"
            onClick={onClear}
            className="text-sm font-semibold text-[#A02448]"
          >
            Clear
          </button>
          <YhButton size="sm" onClick={() => onApply(draft)}>
            Apply
          </YhButton>
        </div>
      </aside>
    </div>
  );
}

function AssignModal({
  open,
  onClose,
  selectedCount,
  total,
  scope,
  departments,
  teams,
  members,
  onTeamsChange,
  onAssign,
}: {
  open: boolean;
  onClose: () => void;
  selectedCount: number;
  total: number;
  scope: Scope;
  departments: Opt[];
  teams: TeamOpt[];
  members: MemberOpt[];
  onTeamsChange?: (teamIds: string[]) => void;
  onAssign: (
    mode: "selected" | "all",
    teamIds: string[],
    userIds: string[]
  ) => Promise<void>;
}) {
  const [mode, setMode] = useState<"selected" | "all">("selected");
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(selectedCount ? "selected" : "all");
    setDepartmentIds([]);
    setUserIds([]);
    if (scope.leader) {
      setTeamIds((scope.teamIds || []).map(String));
    } else {
      setTeamIds([]);
    }
  }, [open, selectedCount, scope.leader, scope.teamIds]);

  const teamOptions = departmentIds.length
    ? teams.filter((t) => departmentIds.includes(String(t.departmentId || "")))
    : teams;
  const memberOptions = (
    teamIds.length ? members.filter((m) => m.teamIds.some((id) => teamIds.includes(id))) : []
  ).map((m) => ({ label: m.label, value: m.value }));

  return (
    <YhModal
      open={open}
      onClose={onClose}
      title="Assign rows"
      width={520}
      footer={
        <>
          <YhButton variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </YhButton>
          <YhButton
            size="sm"
            loading={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onAssign(mode, teamIds, userIds);
              } finally {
                setSaving(false);
              }
            }}
          >
            Assign
          </YhButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-[#6B7280]">
          {scope.leader
            ? "Your team is locked. Choose members on your team."
            : "Choose a department, then a team, then the members who should work these rows."}
        </p>
        <div className="flex flex-col gap-2 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={mode === "selected"}
              disabled={!selectedCount}
              onChange={() => setMode("selected")}
            />
            Selected rows ({selectedCount})
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={mode === "all"}
              onChange={() => setMode("all")}
            />
            All matching rows in this dataset ({total})
          </label>
        </div>
        {scope.orgWide ? (
          <YhMultiSelect
            label="Department"
            placeholder="Select department"
            searchPlaceholder="Search departments..."
            showOptionIcon={false}
            clearable
            options={departments}
            value={departmentIds}
            onChange={(values) => {
              const next = values.map(String);
              setDepartmentIds(next);
              setTeamIds((prev) =>
                prev.filter((id) =>
                  teams.some(
                    (t) =>
                      t.value === id &&
                      (!next.length || next.includes(String(t.departmentId || "")))
                  )
                )
              );
              setUserIds([]);
            }}
          />
        ) : null}
        <YhMultiSelect
          label="Team"
          placeholder={scope.leader ? "Your team" : "Select team"}
          searchPlaceholder="Search teams..."
          showOptionIcon={false}
          clearable={!scope.leader}
          disabled={!!scope.leader}
          options={
            scope.leader
              ? teams.filter((t) => (scope.teamIds || []).includes(t.value))
              : teamOptions
          }
          value={teamIds}
          onChange={(values) => {
            if (scope.leader) return;
            const next = values.map(String);
            setTeamIds(next);
            setUserIds([]);
            onTeamsChange?.(next);
          }}
        />
        <YhMultiSelect
          label="Member"
          placeholder={teamIds.length ? "Select members" : "Select a team first"}
          searchPlaceholder="Search members..."
          showOptionIcon={false}
          clearable
          options={memberOptions}
          value={userIds}
          onChange={(values) => setUserIds(values.map(String))}
        />
      </div>
    </YhModal>
  );
}

export default CallDatasetsPage;
